import { Button } from "@msuite/ui/button";
import { DateInputCalendar } from "@msuite/ui/date-input";
import { Divider } from "@msuite/ui/divider";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { usePopoverContext } from "@msuite/ui/popover";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import type { PatchTicketDatesSchema } from "@msuite/utils/schemas";
import { useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface TicketCardDateCalendarProps {
	ticketId: string;
	projectId: string;
	startDate: string;
	endDate: string;
	dateMode: "date" | "week" | null;
}

export function TicketCardDateCalendar({
	ticketId,
	projectId,
	startDate,
	endDate,
	dateMode,
}: TicketCardDateCalendarProps) {
	/** Context */
	const popover = usePopoverContext();

	/** Hooks */
	const loader = useLoading();
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<PatchTicketDatesSchema>({
		defaultValues: { startDate, endDate, dateMode: dateMode ?? "date" },
	});

	/** Computed */
	const watchedStart = methods.watch("startDate");
	const watchedEnd = methods.watch("endDate");
	const watchedMode = (methods.watch("dateMode") ?? "date") as "date" | "week";

	// In week mode each picked day stands for its whole week, so the highlight reaches that week's
	// ends. Both dates are always set — the columns are NOT NULL — so neither side is absent.
	const highlightRange = (() => {
		if (watchedMode !== "week") return { start: watchedStart, end: watchedEnd };
		return {
			start: moment(watchedStart).startOf("isoWeek").format("YYYY-MM-DD"),
			end: moment(watchedEnd).endOf("isoWeek").format("YYYY-MM-DD"),
		};
	})();

	/** Functions */
	async function handleOnSubmit() {
		try {
			loader.start();
			const json = methods.getValues();
			const response = await client.tickets[":ticketId"].dates.$patch({
				param: { ticketId },
				json,
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate([ticketId, projectId]) });
			popover.handleOnClose();
		} catch (error) {
			console.error(error);
		} finally {
			loader.stop();
		}
	}

	/** Render */
	return (
		<div className="v-stack">
			<div className="h-stack items-start gap-4">
				<div className="v-stack gap-0">
					<div className="h-stack justify-between items-center px-1 pb-1">
						<p className="label">{t("dates.start_date")}</p>
					</div>
					<Divider
						variant="card"
						className="mb-2"
					/>
					<DateInputCalendar
						control={methods.control}
						name="startDate"
						highlightRange={highlightRange}
					/>
				</div>
				<div className="w-2 self-stretch stripes border-x-[0.5px] border-color -my-2" />
				<div className="v-stack gap-0">
					<div className="h-stack justify-between items-center px-1 pb-1">
						<p className="label">{t("dates.end_date")}</p>
					</div>
					<Divider
						variant="card"
						className="mb-2"
					/>
					<DateInputCalendar
						control={methods.control}
						name="endDate"
						highlightRange={highlightRange}
					/>
				</div>
			</div>
			<Divider />
			<div className="h-stack w-full">
				<SegmentedControl
					value={watchedMode}
					onChange={(v) => methods.setValue("dateMode", v)}
					items={[
						{ value: "date", label: t("dates.date"), cyId: "date-mode-date" },
						{ value: "week", label: t("dates.week"), cyId: "date-mode-week" },
					]}
				/>
				<div className="flex-1" />
				<Button
					size="xxs"
					variant="primary"
					isLoading={loader.isLoading}
					onClick={handleOnSubmit}
				>
					{t("general.submit")}
				</Button>
			</div>
		</div>
	);
}
